import {
  SKILL_SOURCE_GITHUB_CHECK_UPDATE_PATH_SEGMENT,
  SKILL_SOURCE_GITHUB_PATH_SEGMENT,
  SKILL_SOURCE_GITHUB_UPDATE_PATH_SEGMENT,
  SKILLS_BASE_PATH,
  SKILLS_CHAT_PICKER_PATH,
} from "../constants/skills";
import type {
  TCheckSkillGithubUpdateRequest,
  TCreateSkillRequest,
  TDeleteSkillResponse,
  TImportGithubRequest,
  TImportPasteRequest,
  TImportZipRequest,
  TListSkillPickerQuery,
  TListSkillsQuery,
  TPresignSkillResourceRequest,
  TPresignSkillResourceResponse,
  TPresignSkillZipRequest,
  TPresignSkillZipResponse,
  TPreviewGithubImportRequest,
  TPreviewPasteImportRequest,
  TPreviewZipImportRequest,
  TReleaseSkillUploadsRequest,
  TReleaseSkillUploadsResponse,
  TSkillBundlePreview,
  TSkillDetail,
  TSkillGithubUpdateCheck,
  TSkillMutationResponse,
  TSkillPastePreview,
  TSkillPickerListResponse,
  TSkillResource,
  TSkillResourceDownload,
  TSkillsListResponse,
  TSkillWithResourcesMutationResponse,
  TUpdateSkillGithubSourceRequest,
  TUpdateSkillRequest,
  TUpdateSkillResourcesRequest,
} from "../types/skills";
import { encodePathSegment } from "../utils/encode-path-segment";
import type { ApiClient } from "./client";

export interface IUpdateSkillParams {
  uuid: string;
  payload: TUpdateSkillRequest;
}

export interface IPresignSkillResourceParams {
  skillUuid: string;
  payload: TPresignSkillResourceRequest;
}

export interface IUpdateSkillResourcesParams {
  skillUuid: string;
  payload: TUpdateSkillResourcesRequest;
}

export interface ICheckSkillGithubUpdateParams {
  skillUuid: string;
  payload: TCheckSkillGithubUpdateRequest;
}

export interface IUpdateSkillGithubSourceParams {
  skillUuid: string;
  payload: TUpdateSkillGithubSourceRequest;
}

export interface IGetSkillResourceDownloadParams {
  skillUuid: string;
  resourceUuid: string;
}

// NOTE: Every uuid reaching a path segment is encoded. A uuid is server-issued
// today, but a client also carries uuids that came from a URL or a paste, and
// an unencoded segment would let one of those change the route.

// NOTE: NSAI-3305 — the Agent Skills HTTP client shared by web, mobile and the
// browser extension.
//
// Two rules this class keeps: it makes NO authorization decision (the server
// decides visibility from the stored row, and a 404 for an invisible skill is
// deliberate), and it moves NO file bytes (uploads go straight to the presigned
// S3 URL, so there is no upload helper here).
//
// Sharing (NSAI-3309) and the organization catalog (NSAI-3311) have types in
// types/skills.ts but deliberately no method here: those routes do not exist.
export class SkillsApi {
  constructor(private readonly apiClient: ApiClient) {}

  async createSkill(
    payload: TCreateSkillRequest,
  ): Promise<TSkillMutationResponse> {
    const response = await this.apiClient.post<TSkillMutationResponse>(
      SKILLS_BASE_PATH,
      payload,
    );
    return response.data;
  }

  async getSkills({
    page,
    limit,
    archived,
    visibility,
    search,
    source,
    enabled,
  }: TListSkillsQuery = {}): Promise<TSkillsListResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) {
      params.set("page", String(page));
    }
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (archived) {
      params.set("archived", archived);
    }
    if (visibility) {
      params.set("visibility", visibility);
    }
    // NOTE: A trimmed-empty needle is omitted rather than sent as `search=`.
    // The server would treat it as "no filter" either way, but leaving it out
    // keeps the query string — and therefore the React Query cache key — the
    // same as an untouched search box.
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params.set("search", trimmedSearch);
    }
    if (source) {
      params.set("source", source);
    }
    if (enabled) {
      params.set("enabled", enabled);
    }

    const query = params.toString();
    const path = query ? `${SKILLS_BASE_PATH}?${query}` : SKILLS_BASE_PATH;

    const response = await this.apiClient.get<TSkillsListResponse>(path);
    return response.data;
  }

  // NOTE: NSAI-3308 — the chat picker's own read. Separate from `getSkills` on
  // purpose: this route answers only skills the runtime would actually load
  // (enabled, not archived, current version, authorized) and searches the
  // description as well as the name.
  async getChatPickerSkills({
    page,
    limit,
    search,
    names,
    uuids,
  }: TListSkillPickerQuery = {}): Promise<TSkillPickerListResponse> {
    const params = new URLSearchParams();
    if (page !== undefined) {
      params.set("page", String(page));
    }
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      params.set("search", trimmedSearch);
    }
    // NOTE: NSAI-5840 — repeated `names=` rather than one joined value, because
    // the server's DTO reads the key as a list (a single occurrence arrives as a
    // string, several as an array) and a skill name may itself contain the
    // separator any joining would pick. Empty entries are dropped here so a
    // half-typed token cannot turn the whole request into "match nothing".
    names
      ?.map((name) => name.trim())
      .filter((name) => name.length > 0)
      .forEach((name) => {
        params.append("names", name);
      });
    // NOTE: NSAI-6395 — repeated `uuids=` for the same reason as `names` above.
    // Empty entries are dropped so a selection holding a blank cannot turn the
    // whole request into "match nothing" and blank out every chip.
    uuids
      ?.map((uuid) => uuid.trim())
      .filter((uuid) => uuid.length > 0)
      .forEach((uuid) => {
        params.append("uuids", uuid);
      });

    const query = params.toString();
    const path = query
      ? `${SKILLS_CHAT_PICKER_PATH}?${query}`
      : SKILLS_CHAT_PICKER_PATH;

    const response = await this.apiClient.get<TSkillPickerListResponse>(path);
    return response.data;
  }

  async getSkill(uuid: string): Promise<TSkillDetail> {
    const response = await this.apiClient.get<TSkillDetail>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  async updateSkill({
    uuid,
    payload,
  }: IUpdateSkillParams): Promise<TSkillMutationResponse> {
    const response = await this.apiClient.patch<TSkillMutationResponse>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(uuid)}`,
      payload,
    );
    return response.data;
  }

  async deleteSkill(uuid: string): Promise<TDeleteSkillResponse> {
    const response = await this.apiClient.delete<TDeleteSkillResponse>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  async previewPasteImport(
    payload: TPreviewPasteImportRequest,
  ): Promise<TSkillPastePreview> {
    const response = await this.apiClient.post<TSkillPastePreview>(
      `${SKILLS_BASE_PATH}/import/paste/preview`,
      payload,
    );
    return response.data;
  }

  async importPaste(
    payload: TImportPasteRequest,
  ): Promise<TSkillMutationResponse> {
    const response = await this.apiClient.post<TSkillMutationResponse>(
      `${SKILLS_BASE_PATH}/import/paste`,
      payload,
    );
    return response.data;
  }

  async presignZipUpload(
    payload: TPresignSkillZipRequest,
  ): Promise<TPresignSkillZipResponse> {
    const response = await this.apiClient.post<TPresignSkillZipResponse>(
      `${SKILLS_BASE_PATH}/import/zip/presign`,
      payload,
    );
    return response.data;
  }

  async previewZipImport(
    payload: TPreviewZipImportRequest,
  ): Promise<TSkillBundlePreview> {
    const response = await this.apiClient.post<TSkillBundlePreview>(
      `${SKILLS_BASE_PATH}/import/zip/preview`,
      payload,
    );
    return response.data;
  }

  async importZip(
    payload: TImportZipRequest,
  ): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.post<TSkillWithResourcesMutationResponse>(
        `${SKILLS_BASE_PATH}/import/zip`,
        payload,
      );
    return response.data;
  }

  async previewGithubImport(
    payload: TPreviewGithubImportRequest,
  ): Promise<TSkillBundlePreview> {
    const response = await this.apiClient.post<TSkillBundlePreview>(
      `${SKILLS_BASE_PATH}/import/github/preview`,
      payload,
    );
    return response.data;
  }

  async importGithub(
    payload: TImportGithubRequest,
  ): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.post<TSkillWithResourcesMutationResponse>(
        `${SKILLS_BASE_PATH}/import/github`,
        payload,
      );
    return response.data;
  }

  // NOTE: NSAI-4961 — asks whether the repository this skill was imported from has
  // moved. It WRITES NOTHING; the answer carries the new commit, the previous one,
  // the changed paths and the ordinary bundle preview, and a person confirms before
  // anything is imported.
  async checkGithubSourceUpdate({
    skillUuid,
    payload,
  }: ICheckSkillGithubUpdateParams): Promise<TSkillGithubUpdateCheck> {
    const response = await this.apiClient.post<TSkillGithubUpdateCheck>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/${SKILL_SOURCE_GITHUB_PATH_SEGMENT}/${SKILL_SOURCE_GITHUB_CHECK_UPDATE_PATH_SEGMENT}`,
      payload,
    );
    return response.data;
  }

  // NOTE: The explicit confirmation of the check above. It appends a NEW immutable
  // version — no existing version is ever changed, and there is no automatic
  // update anywhere in this flow.
  async updateFromGithubSource({
    skillUuid,
    payload,
  }: IUpdateSkillGithubSourceParams): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.post<TSkillWithResourcesMutationResponse>(
        `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/${SKILL_SOURCE_GITHUB_PATH_SEGMENT}/${SKILL_SOURCE_GITHUB_UPDATE_PATH_SEGMENT}`,
        payload,
      );
    return response.data;
  }

  async releaseResourceUploads(
    payload: TReleaseSkillUploadsRequest,
  ): Promise<TReleaseSkillUploadsResponse> {
    const response = await this.apiClient.post<TReleaseSkillUploadsResponse>(
      `${SKILLS_BASE_PATH}/resources/release`,
      payload,
    );
    return response.data;
  }

  async getSkillResources(skillUuid: string): Promise<TSkillResource[]> {
    const response = await this.apiClient.get<TSkillResource[]>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/resources`,
    );
    return response.data;
  }

  async presignResourceUpload({
    skillUuid,
    payload,
  }: IPresignSkillResourceParams): Promise<TPresignSkillResourceResponse> {
    const response = await this.apiClient.post<TPresignSkillResourceResponse>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/resources/presign`,
      payload,
    );
    return response.data;
  }

  async updateResources({
    skillUuid,
    payload,
  }: IUpdateSkillResourcesParams): Promise<TSkillWithResourcesMutationResponse> {
    const response =
      await this.apiClient.put<TSkillWithResourcesMutationResponse>(
        `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/resources`,
        payload,
      );
    return response.data;
  }

  async getResourceDownload({
    skillUuid,
    resourceUuid,
  }: IGetSkillResourceDownloadParams): Promise<TSkillResourceDownload> {
    const response = await this.apiClient.get<TSkillResourceDownload>(
      `${SKILLS_BASE_PATH}/${encodePathSegment(skillUuid)}/resources/${encodePathSegment(
        resourceUuid,
      )}/download`,
    );
    return response.data;
  }
}
